import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useCallback, useMemo } from 'react';
import { isNil } from 'lodash';

import type { UserProfile } from '#packages/web/types/user-profile';
import { mapUserToUserProfile } from '#apps/client-web/app/mappers/user-profile';

import { clientId, logoutRedirectUri } from '../config/auth';

import { useLogger } from './use-logger';

export const useAuth = () => {
  const logger = useLogger('app/hooks/use-auth');

  const {
    isAuthenticated,
    isLoading,
    signinRedirect,
    signoutRedirect,
    user,
    error,
  } = useOidcAuth();

  const profile = useMemo<UserProfile | undefined>(
    () => mapUserToUserProfile(user),
    [user],
  );

  const signInCallback = useCallback(() => {
    signinRedirect()
      .then(() => {
        logger.info('Redirecting to the sign in page...');
      })
      .catch((err) => {
        logger.error(err, 'Error signing in user:');
      });
  }, [signinRedirect]);

  const signOutCallback = useCallback(() => {
    signoutRedirect({
      extraQueryParams: {
        client_id: clientId,
        logout_uri: logoutRedirectUri,
      },
    })
      .then(() => {
        logger.info('Redirecting to the sign out page...');
      })
      .catch((err) => {
        logger.error(err, 'Error signing out user:');
      });
  }, [signoutRedirect]);

  return {
    isLoading,

    isError: !isNil(error),
    error,

    isSignedIn: isAuthenticated === true,
    isNotSignedIn: isAuthenticated !== true,

    signIn: signInCallback,
    signOut: signOutCallback,

    user: user,
    profile: profile,
  };
};
